import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList, SensorWithOrderInfo } from 'src/interfaces'
import { Entities } from 'src/enums'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

export type SensorUploadSensorsType = 'imported' | 'skipped'

interface RequestParams {
  uploadId: number;
  type: SensorUploadSensorsType;
  params?: Partial<AdminSensorUploadSensorsGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<APIList<SensorWithOrderInfo>, Error>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminSensorUploadSensorsGetParams {
  limit: number;
  offset: number;
  sort: string;
  orderNumber: string;
  orderPart: number;
  search: string;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  orderNumber: 'string',
  orderPart: 'number',
  search: 'string',
}

export const useSensorUploadSensorsListRequest = ({
  uploadId,
  type,
  params = {},
  paramsTransform = [],
  options,
}: RequestParams) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminSensorUploadSensorsGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<APIList<SensorWithOrderInfo>, Error>(
    [Entities.SensorUploads, uploadId, 'sensors', type, JSON.stringify(transformedParams)],
    async () => {
      const { limit, offset, sort, orderNumber, orderPart, search } = queryParams
      const { data } = await api.v1AdminSensorUploadsUploadIdSensorsGet(
        uploadId,
        type,
        orderNumber,
        orderPart,
        search,
        limit,
        offset,
        sort,
      )
      return data as unknown as APIList<SensorWithOrderInfo>
    },
    { ...options },
  )
}
